/* eslint-disable react/prop-types */
import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Field, Form as FinalForm, FormSpy, useFormState } from 'react-final-form';
import Form from '../../../../Form/Form';
import { FormattedMessage, useIntl } from 'react-intl';
import IconCheckmark from '../../../../IconCheckmark/IconCheckmark';

import css from './RangeFilterForm.module.css';

const DEBOUNCE_WAIT_TIME = 400;

/**
 * parseToNumber:
 * - accepts string or number
 * - supports comma or dot decimal separators and optional thousand separators
 * - returns numeric value rounded to 1 decimal, or '' for invalid/empty
 */
const parseToNumber = value => {
  if (value === '' || value == null) return '';
  if (typeof value === 'number') {
    const rounded = Math.round(value * 10) / 10;
    return Number.isNaN(rounded) ? '' : rounded;
  }

  let raw = String(value).trim();
  if (raw === '') return '';

  // Remove spaces (thousands separators may be spaces)
  raw = raw.replace(/\s/g, '');

  const hasComma = raw.indexOf(',') !== -1;
  const hasDot = raw.indexOf('.') !== -1;

  // If both present, assume dot = thousands, comma = decimal (e.g. "1.234,5")
  if (hasComma && hasDot) {
    raw = raw.replace(/\./g, '');
    raw = raw.replace(',', '.');
  } else if (hasComma) {
    // only comma present -> decimal separator
    raw = raw.replace(',', '.');
  } // else only dot or none -> treat dot as decimal

  const num = parseFloat(raw);
  if (Number.isNaN(num)) return '';
  return Math.round(num * 10) / 10;
};

const validateRange = values => {
  const errors = {};
  const min = parseToNumber(values.min) || 0;
  const max = parseToNumber(values.max) || Number.MAX_VALUE;

  if (min !== '' && max !== '' && min > max) {
    errors.priceRange = 'RangeFilter.invalidRange';
  }

  return errors;
};

/*
  RangeFilterFields — uses useFormState safely in a real React component.
*/
const RangeFilterFields = ({
  form,
  id,
  showAsPopup = false,
  isOpen = false,
  isInSideBar = false,
  contentRef,
  style,
  min = 0,
  max = 1000,
  step = 0.1, // allow one-decimal increments
  unit,
  children,
  className,
  formatNumber = true,
  minLabel,
  maxLabel,
  disableMin = false,
  compactFilter = false,
  presets,
  liveEdit,
  onClear,
  onCancel,
  handleChange, // debounced liveEdit handler
  fromLabel,
}) => {
  const intl = useIntl();

  // subscribe only to what we need
  const { values: formValues = {}, errors = {}, invalid } = useFormState({
    subscription: { values: true, errors: true, invalid: true },
  });

  const minVal =
    disableMin || typeof formValues.min !== 'number' ? (disableMin ? 0 : min) : formValues.min;
  const maxVal = typeof formValues.max === 'number' ? formValues.max : max;

  const defaultPresets = [];
  const effectivePresets =
    presets === undefined ? defaultPresets : Array.isArray(presets) ? presets : null;
  const showPresets = Array.isArray(effectivePresets) && effectivePresets.length > 0;

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const clearText = intl.formatMessage({ id: 'RangeFilterForm.reset' });
  const cancelText = intl.formatMessage({ id: 'RangeFilterForm.cancel' });
  const submitText = intl.formatMessage({ id: 'RangeFilterForm.submit' });

  const classes = classNames(css.root, className, {
    [css.popup]: showAsPopup,
    [css.isOpenAsPopup]: showAsPopup && isOpen,
    [css.plain]: !showAsPopup,
    [css.isOpen]: !showAsPopup && isOpen,
  });

  // helper to format numeric values for display with up to 1 decimal
  const formatDisplayNumber = num =>
    intl.formatNumber(num, { maximumFractionDigits: 1, minimumFractionDigits: 0 });

  /**
   * renderNumericField:
   * - while typing: we keep the raw string (so partial input like "1," is preserved)
   * - we visually convert any typed '.' to ',' so UI consistently shows comma
   * - on blur: we coerce to numeric (number rounded to 1 decimal) or '' if invalid/empty
   */
  const renderNumericField = (name, placeholderValue, fieldClass) => (
    <Field name={name}>
      {({ input, meta }) => {
        // input.value might be number (after blur / preset) or string (while typing)
        const displayValue =
          typeof input.value === 'number'
            ? formatNumber
              ? formatDisplayNumber(Math.round(input.value * 10) / 10)
              : String(Math.round(input.value))
            : input.value != null
            ? String(input.value).replace(/\./g, ',')
            : '';

        return (
          <input
            {...input}
            id={`${id}.${name}`}
            className={fieldClass}
            type="text"
            inputMode="decimal"
            pattern="[0-9]*[.,]?[0-9]*"
            placeholder={'-'}
            step={step}
            value={displayValue}
            onChange={e => {
              // keep raw typed value in the field state while typing
              // convert any typed '.' to ',' for consistent display
              const typed = e.target.value;
              const display = typed.replace(/\./g, ',');
              // store the raw display string (with comma) — parse/validate happens elsewhere (onBlur and in handleChange/submit)
              input.onChange(display);
            }}
            onBlur={e => {
              // on blur, convert to a numeric value rounded to 1 decimal or empty string if invalid
              const parsed = parseToNumber(input.value);
              input.onChange(parsed === '' ? '' : parsed);
              input.onBlur && input.onBlur(e);
            }}
          />
        );
      }}
    </Field>
  );

  return (
    <Form
      className={classes}
      onSubmit={form.handleSubmit}
      tabIndex="0"
      contentRef={contentRef}
      style={style}
    >
      <div className={css.contentWrapper}>
        {showAsPopup && (
          <span className={css.label}>
            <FormattedMessage id="RangeFilterForm.label" />
          </span>
        )}

        <div
          className={classNames(css.inputsWrapper, { [css.inputsWrapperCompact]: compactFilter })}
        >
          {!disableMin && (
            <div className={classNames(css.inputRow, { [css.compactInputRow]: compactFilter })}>
              <label htmlFor={`${id}.min`}>{minLabel || 'Min'}</label>
              {!compactFilter ? <span>{unit}</span> : <span />}
              {renderNumericField(
                'min',
                min,
                classNames(css.minField, { [css.inputInSidebar]: isInSideBar })
              )}
            </div>
          )}

          <div className={classNames(css.inputRow, { [css.compactInputRow]: compactFilter })}>
            <label htmlFor={`${id}.max`}>{maxLabel || 'Max'}</label>
            {!compactFilter ? <span>{unit}</span> : <span />}
            {renderNumericField(
              'max',
              max,
              classNames(css.maxField, { [css.inputInSidebar]: isInSideBar })
            )}
          </div>
        </div>
      </div>

      {/* One universal form-level error shown under inputs */}
      {errors?.priceRange && (
        <div className={css.error}>{intl.formatMessage({ id: errors.priceRange })}</div>
      )}

      {!compactFilter && <div className={css.divider} />}

      {/* PRESETS */}
      {showPresets && (
        <>
          <div className={css.presetsTitle}>
            <FormattedMessage id="Global.mostFrequent" />
          </div>
          <div className={css.presets}>
            {effectivePresets.map(([pMin, pMax], i) => (
              <button
                key={i}
                type="button"
                className={css.presetButton}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  form.change('min', disableMin ? 0 : pMin);
                  form.change('max', pMax);
                }}
              >
                {minVal === pMin && maxVal === pMax && <IconCheckmark size="small" />}
                {pMin === min && fromLabel
                  ? `${fromLabel} ${
                      formatNumber
                        ? intl.formatNumber(pMax, {
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 0,
                          })
                        : pMax
                    } ${unit}`
                  : pMax === max
                  ? `${
                      formatNumber
                        ? intl.formatNumber(pMin, {
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 0,
                          })
                        : pMin
                    } ${unit} +`
                  : `${
                      formatNumber
                        ? intl.formatNumber(pMin, {
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 0,
                          })
                        : pMin
                    } - ${
                      formatNumber
                        ? intl.formatNumber(pMax, {
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 0,
                          })
                        : pMax
                    } ${unit}`}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Buttons / liveEdit */}
      {liveEdit ? (
        // FormSpy just triggers parent's handleChange (debounced) — that handler parses values before sending
        <FormSpy
          onChange={handleChange}
          subscription={{ values: true, dirty: true, invalid: true }}
        />
      ) : (
        <div className={css.buttonsWrapper}>
          <button className={css.clearButton} type="button" onClick={onClear}>
            {clearText}
          </button>
          <button className={css.cancelButton} type="button" onClick={handleCancel}>
            {cancelText}
          </button>
          <button className={css.submitButton} type="submit" disabled={invalid}>
            {submitText}
          </button>
        </div>
      )}

      {children}
    </Form>
  );
};

const RangeFilterForm = props => {
  const intl = useIntl();
  const {
    liveEdit = false,
    onChange,
    onSubmit,
    onCancel,
    onClear,
    presets,
    disableMin = false,
    compactFilter = false,
    ...rest
  } = props;

  if (liveEdit && !onChange) {
    throw new Error('RangeFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'RangeFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  // handleSubmit used for non-live submit — convert values to numbers before sending
  const handleSubmit = values => {
    const { min: rawMin, max: rawMax, ...restValues } = values || {};
    const parsedMin = disableMin ? 0 : parseToNumber(rawMin);
    const parsedMax = parseToNumber(rawMax);

    const effectiveMin = parsedMin === '' ? (rest.min ?? disableMin ? 0 : rest.min) : parsedMin;
    const effectiveMax = parsedMax === '' ? rest.max : parsedMax;

    return onSubmit({
      ...(effectiveMin !== '' && { min: effectiveMin }),
      ...(effectiveMax !== '' && { max: effectiveMax }),
      ...restValues,
    });
  };

  // debounced onChange for liveEdit (calls parent's onChange)
  // IMPORTANT: parse values before sending so backend receives numbers (not "1,5" strings)
  const handleChange = useMemo(
    () =>
      debounce(formState => {
        if (!formState) return;
        // Validate range first
        if (validateRange(formState.values).priceRange) return;

        // Only send when dirty and valid
        if (formState?.dirty && !formState.invalid) {
          const { min: rawMin, max: rawMax, ...restValues } = formState.values || {};
          const parsedMin = disableMin ? 0 : parseToNumber(rawMin);
          const parsedMax = parseToNumber(rawMax);

          const payload = {
            ...(parsedMin !== '' && { min: parsedMin }),
            ...(parsedMax !== '' && { max: parsedMax }),
            ...restValues,
          };

          // only call onChange if there's something meaningful
          if (Object.keys(payload).length > 0) {
            onChange(payload);
          }
        }
      }, DEBOUNCE_WAIT_TIME),
    [onChange, disableMin, rest.min, rest.max]
  );

  // cancel debounce on unmount
  useEffect(() => {
    return () => {
      handleChange.cancel();
    };
  }, [handleChange]);

  const formCallbacks = liveEdit
    ? { onSubmit: () => null }
    : { onSubmit: handleSubmit, onCancel, onClear };

  return (
    <FinalForm
      {...rest}
      {...formCallbacks}
      validate={validateRange}
      render={formRenderProps => (
        <RangeFilterFields
          {...formRenderProps}
          presets={presets}
          disableMin={disableMin}
          compactFilter={compactFilter}
          liveEdit={liveEdit}
          onClear={onClear}
          onCancel={onCancel}
          handleChange={handleChange}
        />
      )}
    />
  );
};

export default RangeFilterForm;
